// src/Components/HighLevelComponents/Tool/renderers/MCQRenderer.tsx
import React, { useState, useContext } from "react";
import { ToolRendererProps } from "../types";
import { FaCopy, FaFileExport, FaFileWord, FaDownload } from "react-icons/fa";
import { BiExport } from "react-icons/bi";
import toast from "react-hot-toast";
import { GoogleAnalyticsContext } from "../../../../Hooks/GoogleAnalyticsContext";

export interface MCQResult {
  question: string;
  options: string | string[]; // Ensure options can be a string or string[]
  answer: string;
}

interface MCQQuestionProps {
  question: string;
  options: string | string[];
  answer: string;
  index: number;
  isPracticeMode: boolean;
}

const MCQQuestion: React.FC<MCQQuestionProps> = ({
  question,
  options,
  answer,
  index,
  isPracticeMode,
}) => {
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Parse options to ensure we have an array
  const parsedOptions = Array.isArray(options)
    ? options
    : typeof options === "string"
    ? options.split(/\n|\\n/).filter((opt: string) => opt.trim().length > 0)
    : [];

  // Handle option selection
  const handleOptionSelect = (option: string) => {
    if (isCorrect !== null) return; // Don't allow changes after checking

    setSelectedOption(option);
  };

  // Check if the selected answer is correct
  const checkAnswer = () => {
    if (!selectedOption) return;

    const normalizedSelected = selectedOption.toLowerCase().trim();
    const normalizedAnswer = answer.toLowerCase().trim();

    // Check if the selected option contains the answer or vice versa
    const correct =
      normalizedSelected.includes(normalizedAnswer) ||
      normalizedAnswer.includes(normalizedSelected);

    setIsCorrect(correct);
    setShowAnswer(true);

    // Log answer result for analytics
    logEvent(
      "MCQInteraction",
      correct ? "AnswerCorrect" : "AnswerIncorrect",
      `mcq_question_${index + 1}`
    );
  };

  // Reset the question
  const resetQuestion = () => {
    setSelectedOption(null);
    setIsCorrect(null);
    setShowAnswer(false);
    logEvent("MCQInteraction", "Reset", `mcq_question_${index + 1}`);
  };

  // Copy question and answer to clipboard
  const copyToClipboard = async () => {
    try {
      let text = `Question: ${question}\n\nOptions:\n`;

      // Format options
      parsedOptions.forEach((option, i) => {
        text += `${String.fromCharCode(65 + i)}. ${option}\n`;
      });

      text += `\nAnswer: ${answer}`;

      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
      logEvent("Export", "CopyMCQToClipboard", `mcq_question_${index + 1}`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-4">
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-semibold text-gray-800 text-lg">
          Question {index + 1}
        </h3>
        <div className="flex items-center">
          <button
            onClick={copyToClipboard}
            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none mr-2"
            title="Copy to clipboard"
          >
            <FaCopy size={18} />
          </button>
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded">
            MCQ
          </span>
        </div>
      </div>

      <p className="text-gray-700 mb-4">{question}</p>

      <div className="space-y-2 mb-4">
        {parsedOptions.map((option, i) => (
          <div
            key={i}
            onClick={() => isPracticeMode && handleOptionSelect(option)}
            className={`
              p-3 rounded-lg border cursor-pointer transition-colors
              ${
                selectedOption === option
                  ? "border-yellow-400 bg-yellow-50"
                  : isPracticeMode
                  ? "border-gray-200 hover:bg-gray-50"
                  : "border-gray-200"
              }
              ${
                showAnswer && isCorrect !== null && option.includes(answer)
                  ? "border-green-400 bg-green-50"
                  : ""
              }
              ${
                !isPracticeMode && option.includes(answer)
                  ? "border-green-400 bg-green-50"
                  : ""
              }
            `}
          >
            <div className="flex items-start">
              <div className="mr-2">
                <div
                  className={`
                  w-5 h-5 rounded-full flex items-center justify-center text-xs
                  ${
                    selectedOption === option
                      ? "bg-yellow-400 text-white"
                      : "bg-gray-200 text-gray-600"
                  }
                  ${
                    showAnswer && isCorrect !== null && option.includes(answer)
                      ? "bg-green-400 text-white"
                      : ""
                  }
                  ${
                    !isPracticeMode && option.includes(answer)
                      ? "bg-green-400 text-white"
                      : ""
                  }
                `}
                >
                  {String.fromCharCode(65 + i)}
                </div>
              </div>
              <p className="text-gray-700">{option}</p>
            </div>
          </div>
        ))}
      </div>

      {isPracticeMode && showAnswer ? (
        <div>
          <div
            className={`
            p-4 rounded-lg mb-4
            ${
              isCorrect
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }
          `}
          >
            <div className="flex items-center">
              <div
                className={`
                w-6 h-6 rounded-full flex items-center justify-center mr-2
                ${isCorrect ? "bg-green-500" : "bg-red-500"} text-white
              `}
              >
                {isCorrect ? "✓" : "✗"}
              </div>
              <p
                className={`font-medium ${
                  isCorrect ? "text-green-700" : "text-red-700"
                }`}
              >
                {isCorrect ? "Correct!" : "Incorrect"}
              </p>
            </div>
            <p className="text-gray-700 mt-2">
              <span className="font-medium">Correct answer:</span> {answer}
            </p>
          </div>

          <button
            onClick={resetQuestion}
            className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Try again
          </button>
        </div>
      ) : isPracticeMode ? (
        <div className="flex justify-between">
          <button
            onClick={resetQuestion}
            className="py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            disabled={!selectedOption}
          >
            Reset
          </button>

          <button
            onClick={checkAnswer}
            className="py-2 px-4 bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-medium rounded-lg transition-colors"
            disabled={!selectedOption}
          >
            Check Answer
          </button>
        </div>
      ) : (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-gray-700">
            <span className="font-medium">Answer:</span> {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const MCQRenderer: React.FC<ToolRendererProps> = ({ results, isLoading }) => {
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const { logEvent } = useContext(GoogleAnalyticsContext);

  const togglePracticeMode = () => {
    setIsPracticeMode(!isPracticeMode);
    logEvent(
      "MCQInteraction",
      !isPracticeMode ? "EnablePracticeMode" : "DisablePracticeMode",
      "mcq"
    );
  };

  // Export to Word-compatible document
  const exportToWordDoc = () => {
    // Create content with proper formatting
    let content = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
<head>
<meta charset="utf-8">
<title>Multiple Choice Questions</title>
<style>
  body { font-family: 'Calibri', sans-serif; }
  .question { font-weight: bold; margin-top: 20px; }
  .options { margin-left: 20px; }
  .option { margin: 5px 0; }
  .answer { margin-bottom: 20px; margin-top: 10px; font-style: italic; }
</style>
</head>
<body>
<h1>Multiple Choice Questions</h1>`;

    // Add each question and answer
    results.forEach((result: MCQResult, index) => {
      const options = Array.isArray(result.options)
        ? result.options
        : result.options.split(/\n|\\n/).filter((opt) => opt.trim().length > 0);

      content += `
<div class="question">Question ${index + 1}: ${result.question}</div>
<div class="options">`;

      options.forEach((option, i) => {
        content += `
  <div class="option">${String.fromCharCode(65 + i)}. ${option}</div>`;
      });

      content += `
</div>
<div class="answer">Answer: ${result.answer}</div>`;
    });

    content += `
</body>
</html>`;

    // Create file and trigger download
    const blob = new Blob([content], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "multiple_choice_questions.doc";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Word document downloaded!");
    logEvent("Export", "ExportToWord", "mcq");
    setExportMenuOpen(false);
  };

  // Export to PDF format
  const exportToPDF = () => {
    // Create a simple HTML structure that will print nicely
    let content = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Multiple Choice Questions</title>
  <style>
    body { font-family: Arial, sans-serif; }
    .question { font-weight: bold; margin-top: 15px; }
    .options { margin-left: 20px; }
    .option { margin: 5px 0; }
    .answer { margin-bottom: 15px; font-style: italic; }
    @media print {
      @page { margin: 1cm; }
    }
  </style>
</head>
<body>
  <h1>Multiple Choice Questions</h1>`;

    // Add each question and answer
    results.forEach((result: MCQResult, index) => {
      const options = Array.isArray(result.options)
        ? result.options
        : result.options.split(/\n|\\n/).filter((opt) => opt.trim().length > 0);

      content += `
  <div class="question">Question ${index + 1}: ${result.question}</div>
  <div class="options">`;

      options.forEach((option, i) => {
        content += `
    <div class="option">${String.fromCharCode(65 + i)}. ${option}</div>`;
      });

      content += `
  </div>
  <div class="answer">Answer: ${result.answer}</div>`;
    });

    content += `
</body>
</html>`;

    // Open a new window with print dialog
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();

      // Give the browser a moment to process the document
      setTimeout(() => {
        printWindow.print();
        // Close the window after print dialog is closed
        printWindow.onafterprint = function () {
          printWindow.close();
        };
      }, 500);

      toast.success("PDF prepared for printing!");
      logEvent("Export", "ExportToPDF", "mcq");
    } else {
      toast.error("Please allow popups to enable PDF export");
    }

    setExportMenuOpen(false);
  };

  // Export as CSV format
  const exportToCSV = () => {
    // Create CSV content with question, options, and answer
    let csvContent = "Question,Option A,Option B,Option C,Option D,Answer\n";

    results.forEach((result: MCQResult) => {
      const options = Array.isArray(result.options)
        ? result.options
        : result.options.split(/\n|\\n/).filter((opt) => opt.trim().length > 0);

      // Escape any commas in the content
      const escapedQuestion = `"${result.question.replace(/"/g, '""')}"`;

      // Create a row with the question and options (padding with empty values if fewer than 4 options)
      let row = [escapedQuestion];

      // Add options (up to 4)
      for (let i = 0; i < 4; i++) {
        if (i < options.length) {
          row.push(`"${options[i].replace(/"/g, '""')}"`);
        } else {
          row.push('""'); // Empty option
        }
      }

      // Add the answer
      row.push(`"${result.answer.replace(/"/g, '""')}"`);

      csvContent += row.join(",") + "\n";
    });

    // Create file and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "multiple_choice_questions.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("CSV file downloaded!");
    logEvent("Export", "ExportToCSV", "mcq");
    setExportMenuOpen(false);
  };

  // Export as text file
  const exportToText = () => {
    let text = "MULTIPLE CHOICE QUESTIONS\n\n";

    results.forEach((result: MCQResult, index) => {
      const options = Array.isArray(result.options)
        ? result.options
        : result.options.split(/\n|\\n/).filter((opt) => opt.trim().length > 0);

      text += `Question ${index + 1}: ${result.question}\n`;
      text += "Options:\n";

      options.forEach((option, i) => {
        text += `${String.fromCharCode(65 + i)}. ${option}\n`;
      });

      text += `Answer: ${result.answer}\n\n`;
    });

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "multiple_choice_questions.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Text file downloaded!");
    logEvent("Export", "ExportToText", "mcq");
    setExportMenuOpen(false);
  };

  // Copy all questions and answers to clipboard
  const copyToClipboard = async () => {
    try {
      let text = "Multiple Choice Questions:\n\n";

      results.forEach((result: MCQResult, index) => {
        const options = Array.isArray(result.options)
          ? result.options
          : result.options
              .split(/\n|\\n/)
              .filter((opt) => opt.trim().length > 0);

        text += `Question ${index + 1}: ${result.question}\n`;
        text += "Options:\n";

        options.forEach((option, i) => {
          text += `${String.fromCharCode(65 + i)}. ${option}\n`;
        });

        text += `Answer: ${result.answer}\n\n`;
      });

      await navigator.clipboard.writeText(text);
      toast.success("All questions copied to clipboard!");
      logEvent("Export", "CopyAllMCQs", "mcq");
      setExportMenuOpen(false);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-700">
          {results.length} Multiple Choice Questions
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={togglePracticeMode}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${
                isPracticeMode
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
            `}
          >
            {isPracticeMode ? "View Mode" : "Practice Mode"}
          </button>

          {/* Export Menu */}
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 font-medium rounded-lg flex items-center transition-colors"
            >
              <BiExport className="mr-2" />
              Export
            </button>

            {exportMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 overflow-hidden border border-gray-100">
                <div className="py-1">
                  <button
                    onClick={copyToClipboard}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaCopy className="mr-2" />
                    Copy all to clipboard
                  </button>
                  <button
                    onClick={exportToWordDoc}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaFileWord className="mr-2" />
                    Export as Word Doc
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaDownload className="mr-2" />
                    Export as PDF
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaFileExport className="mr-2" />
                    Export as CSV
                  </button>
                  <button
                    onClick={exportToText}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaFileExport className="mr-2" />
                    Export as Text
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {results.map((result: MCQResult, index) => (
          <MCQQuestion
            key={index}
            question={result.question}
            options={result.options}
            answer={result.answer}
            index={index}
            isPracticeMode={isPracticeMode}
          />
        ))}
      </div>

      {isPracticeMode && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">Practice Mode Tips</h4>
          <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
            <li>Select your answer and click "Check Answer"</li>
            <li>Correct answers will be highlighted in green</li>
            <li>Click "Try again" to reset and try the question again</li>
            <li>Use "View Mode" to see all correct answers at once</li>
          </ul>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">Export Options</h4>
        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
          <li>
            <strong>Word Document:</strong> Export in Microsoft Word format for
            editing and formatting
          </li>
          <li>
            <strong>PDF Export:</strong> Create a printable PDF document via
            your browser's print function
          </li>
          <li>
            <strong>CSV Export:</strong> Spreadsheet-compatible format, useful
            for importing into LMS systems
          </li>
          <li>
            <strong>Text Export:</strong> Plain text format for all questions
            and answers
          </li>
        </ul>
      </div>
    </div>
  );
};

export default MCQRenderer;
